/**
 * Invoices P1 — pure, deterministic aging math.
 *
 * The DISPLAYED aging buckets and business-wide totals come from EXACT Firestore
 * server aggregates (see Invoices.jsx). This module holds only the pure logic:
 *   • the createdAt range cutoffs used to build those aggregate queries,
 *   • the canonical age→bucket classifier (mutually exclusive + collectively
 *     exhaustive), and
 *   • the reconciliation guardrail that surfaces receivables which lack a valid
 *     Timestamp createdAt (so they are never silently dropped from the aged view).
 *
 * Boundaries (age = now − createdAt, in days):
 *   current: age < 30     30–60: 30 ≤ age < 60
 *   60–90:   60 ≤ age < 90     90+: age ≥ 90
 */

export const DAY_MS = 86_400_000;

/** createdAt cutoffs (Date objects) for the four aging range aggregates. */
export function agingCutoffs(now = Date.now()) {
  return {
    cut30: new Date(now - 30 * DAY_MS),
    cut60: new Date(now - 60 * DAY_MS),
    cut90: new Date(now - 90 * DAY_MS),
  };
}

/**
 * Canonical age→bucket classifier. Used to prove the createdAt range boundaries
 * are mutually exclusive and collectively exhaustive — an exact-boundary age
 * (e.g. exactly 30 days) lands in EXACTLY one bucket.
 * @param {number} days whole/fractional days old
 * @returns {'current'|'d30'|'d60'|'d90'}
 */
export function bucketForAgeDays(days) {
  const d = Number(days) || 0;
  if (d >= 90) return 'd90';
  if (d >= 60) return 'd60';
  if (d >= 30) return 'd30';
  return 'current';
}

/**
 * Reconciliation guardrail. Given the exact type-agnostic total and the four
 * exact aging-range aggregates, return the residual (owed invoices with no valid
 * Timestamp createdAt) or null when everything is accounted for.
 *
 * @param {{count:?number, sum:?number}} total   status-only exact aggregate
 * @param {{current:?{count,sum}, d30:?, d60:?, d90:?}} buckets range aggregates
 * @returns {{count:number, sum:?number}|null}
 */
export function reconcileAgingResidual(total, buckets) {
  if (!total || total.count == null || !buckets) return null;
  const b = ['current', 'd30', 'd60', 'd90'].map(k => buckets[k]);
  // Every bucket must have resolved for the residual to be meaningful.
  if (b.some(x => !x)) return null;
  const bCount = b.reduce((s, x) => s + (x.count || 0), 0);
  const bSum = b.reduce((s, x) => s + (x.sum || 0), 0);
  const rCount = total.count - bCount;
  if (rCount <= 0) return null;
  const rSum = total.sum != null ? +(total.sum - bSum).toFixed(2) : null;
  return { count: rCount, sum: rSum };
}
